import { useAppSelector } from '../app/hooks';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { fetchVisibilityConfig } from '../store/user/userV2/actions';
import type { RoleCatalog } from '../store/user/userV2/reducer';
import type { UserTypeCatalogEntry } from '../modules/Investor/ManageUser/ManageUserConstants';
import { VIEW_CONFIGS } from './constants/commonConstants';
import { useSearchParams } from 'react-router-dom';
import type { VisibilityLevel } from './constants/visibilityKeys';
import { VISIBILITY_LEVEL } from './constants/visibilityKeys';
import * as visibilityConfigUtils from './visibilityConfigUtils';
import type { FeatureVisibility, VisibilityConfigMap } from './visibilityConfigUtils';

export type { FeatureVisibility } from './visibilityConfigUtils';

export const useVisibilityConfig = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const visibilityConfig = useAppSelector((state) => {
        const raw = state.user.visibilityConfig;
        return (raw?.additionalDetailsConfig ?? raw) as VisibilityConfigMap;
    });
    const roleCatalog = (visibilityConfig?.roleCatalog ?? {}) as RoleCatalog;
    const userTypeCatalog = (visibilityConfig?.userTypeCatalog ?? {}) as Record<string, UserTypeCatalogEntry[]>;
    const visibilityConfigLoading = useAppSelector((state) => state.user.visibilityConfigLoading);
    const visibilityConfigLoaded = useAppSelector((state) => state.user.visibilityConfigLoaded);
    // True once /account/config has resolved (payload stored, even if empty). Lets callers
    // avoid the fail-open default (getLevel → READ) that would otherwise flash gated UI
    // visible while the config is still loading.
    const isConfigLoaded = useAppSelector((state) => state.user.visibilityConfig !== null);
    const userData = useAppSelector((state) => state.user.userData);
    // accountType can be overridden via the URL param (e.g. viewing a partner's leads from
    // Manage Partner → ?accountType=PARTNER). The visibility config must reflect the viewed
    // context, so it has to re-fetch when accountType changes on client-side navigation.
    const accountTypeParam = searchParams.get('accountType');
    const effectiveAccountType = accountTypeParam || userData?.accountType;
    // Tracks the accountType the config was last fetched for, so the fetch-once guard
    // below still allows a re-fetch when the context switches.
    const lastFetchedAccountTypeRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (userData?.accountType && userData?.primaryEmail && userData?.investorOrgId) {
            // Fetch once PER accountType context. If a config is already loaded (or in
            // flight), only re-fetch when the accountType CONTEXT has actually changed
            // (e.g. → partner leads). On first mount the ref is still undefined, so adopt the
            // current accountType as the baseline and trust the loaded config — don't
            // re-fetch. (Re-fetching a config already present on mount was both a redundant
            // request and broke consumers whose tests seed a loaded config without mocking
            // the fetch action.)
            if (visibilityConfigLoaded || visibilityConfigLoading) {
                if (lastFetchedAccountTypeRef.current === undefined || lastFetchedAccountTypeRef.current === effectiveAccountType) {
                    lastFetchedAccountTypeRef.current = effectiveAccountType;
                    return;
                }
            }
            lastFetchedAccountTypeRef.current = effectiveAccountType;
            const payload = {
                accountType: effectiveAccountType,
                primaryEmail: userData.primaryEmail,
                investorOrgId: userData.investorOrgId,
                userRole: userData?.roles?.[0] || '',
                configName: VIEW_CONFIGS.AICA_VISIBILITY,
            };

            dispatch(fetchVisibilityConfig(payload));
        }
    }, [
        dispatch,
        effectiveAccountType,
        userData?.accountType,
        userData?.primaryEmail,
        userData?.investorOrgId,
        userData?.roles,
        visibilityConfigLoaded,
        visibilityConfigLoading,
    ]);

    const refreshConfig = () => {
        if (userData?.accountType && userData?.primaryEmail && userData?.investorOrgId) {
            const payload = {
                accountType: userData.accountType,
                primaryEmail: userData.primaryEmail,
                investorOrgId: userData.investorOrgId,
                userRole: userData?.roles?.[0] || '',
                configName: VIEW_CONFIGS.AICA_VISIBILITY,
            };

            dispatch(fetchVisibilityConfig(payload));
        }
    };

    const getLevel = (configKey: string): VisibilityLevel => visibilityConfigUtils.getLevel(visibilityConfig, configKey);

    const isVisible = (configKey: string) => getLevel(configKey) >= VISIBILITY_LEVEL.READ;
    const isHidden = (configKey: string) => getLevel(configKey) === VISIBILITY_LEVEL.HIDDEN;
    const canRead = (configKey: string) => getLevel(configKey) >= VISIBILITY_LEVEL.READ;
    const canWrite = (configKey: string) => getLevel(configKey) === VISIBILITY_LEVEL.WRITE;
    const getFeatureVisibility = useCallback(
        (configKey: string) => visibilityConfigUtils.getFeatureVisibility(visibilityConfig, configKey),
        [visibilityConfig],
    );

    return {
        config: visibilityConfig,
        isConfigLoaded,
        roleCatalog,
        userTypeCatalog,
        getLevel,
        isVisible,
        isHidden,
        canRead,
        canWrite,
        getFeatureVisibility,
        refreshConfig,
    };
};

export function useFeatureVisibility<T extends Record<string, string>>(
    keysObj: T,
): Record<keyof T, FeatureVisibility> & { getFeatureVisibility: (key: string) => FeatureVisibility } {
    const { getFeatureVisibility } = useVisibilityConfig();

    const visibilityMap = useMemo(() => {
        return Object.entries(keysObj).reduce(
            (acc, [alias, key]) => {
                acc[alias as keyof T] = getFeatureVisibility(key);
                return acc;
            },
            {} as Record<keyof T, FeatureVisibility>,
        );
    }, [keysObj, getFeatureVisibility]);

    return { ...visibilityMap, getFeatureVisibility };
}
